import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { HexColorPicker } from "react-colorful";
import { Input } from "semantic-ui-react";

import {
  Category,
  Classification,
  Product,
  Schema,
} from "../../../../../api";
import {
  SSCARACTERISTICA,
  SSINCLUYE,
  SSMARCA,
} from "../../../../../utils";

import styles from "./CreateProductPage.module.scss";

// Antes era 10: muy poco cuando el producto tiene varias variantes con
// fotos propias (una gorra en 5 colores, por ejemplo, ya necesita al
// menos una foto por color además de las generales). No hay ningún
// límite real del servidor -- connect-multiparty solo limita el tamaño
// total, no la cantidad de archivos -- así que este número es puramente
// de UX; se puede subir más si hace falta.
const MAX_IMAGES = 40;
const MAX_VARIANTS = 100;

const INITIAL_VARIANT = {
  sku: "",
  attributes: [],
  price: "",
  offerPrice: "",
  cost: "",
  stock: 0,
  lowStockAlert: 0,
  enabled: true,
  images: [],
};

const INITIAL_VALUES = {
  id: "",
  name: "",
  slug: "",
  category: "",
  brand: "",
  description: "",
  characteristics: [],
  variants: [INITIAL_VARIANT],
  visible: true,
  featured: false,
  seo: {
    metaTitle: "",
    metaDescription: "",
  },
  images: [],
};

function normalizeString(value) {
  return String(value || "").trim();
}

function normalizeKey(value) {
  return normalizeString(value)
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

function createSlug(value) {
  return normalizeString(value)
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/ñ/g, "n")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function createProductId(value) {
  return normalizeString(value)
    .toUpperCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/Ñ/g, "N")
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function createSku(value) {
  return normalizeString(value)
    .toUpperCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/Ñ/g, "N")
    .replace(/[^A-Z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .replace(/-{2,}/g, "-");
}

function buildDefaultMetaTitle(name) {
  return normalizeString(name).slice(0, 60);
}

function buildDefaultMetaDescription(description) {
  return normalizeString(description).replace(/\s+/g, " ").slice(0, 160);
}

function getEntityId(entity) {
  if (!entity) {
    return "";
  }

  if (typeof entity === "object") {
    return String(entity._id || entity.id || "");
  }

  return String(entity);
}

function getCategoriesResponse(result) {
  const response = result?.categories || result?.response || result || {};

  if (Array.isArray(response.docs)) {
    return response.docs;
  }

  if (Array.isArray(response)) {
    return response;
  }

  return [];
}

function getClassificationsResponse(result) {
  const response =
    result?.classifications || result?.response || result || {};

  if (Array.isArray(response.docs)) {
    return response.docs;
  }

  if (Array.isArray(response)) {
    return response;
  }

  return [];
}

function getSchemasResponse(result) {
  const response = result?.schemas || result?.response || result || {};

  if (Array.isArray(response.docs)) {
    return response.docs;
  }

  if (Array.isArray(response)) {
    return response;
  }

  return [];
}

async function fetchClassificationsBySchema(schemaId) {
  const schemaController = new Schema();
  const classificationController = new Classification();

  const schemasResult = await schemaController.getSchemas(1, 1, {
    search: schemaId,
  });

  const schema = getSchemasResponse(schemasResult).find(
    (item) => item?.id === schemaId
  );

  if (!schema) {
    return [];
  }

  const result = await classificationController.getClassifications(
    1,
    100,
    {
      schema: getEntityId(schema),
    }
  );

  return getClassificationsResponse(result);
}

function getCategoryDisplayName(category) {
  const level = Math.max(Number(category?.level || 1) - 1, 0);
  const prefix = level > 0 ? `${"— ".repeat(level)}` : "";

  return `${prefix}${category?.name || ""}`;
}

function createImagePreview(file, order) {
  return {
    localId: crypto.randomUUID(),
    file,
    path: URL.createObjectURL(file),
    name: file.name,
    order,
    isCover: order === 1,
  };
}

function isHexColor(value) {
  return /^#[0-9a-f]{6}$/i.test(normalizeString(value));
}

const PRESET_COLORS = [
  { name: "Negro", hex: "#000000" },
  { name: "Blanco", hex: "#ffffff" },
  { name: "Gris claro", hex: "#b0b0b0" },
  { name: "Gris oscuro", hex: "#4a4a4a" },
  { name: "Beige", hex: "#e8dcc8" },
  { name: "Crema", hex: "#f5eede" },
  { name: "Camel", hex: "#c19a6b" },
  { name: "Café claro", hex: "#8b5e3c" },
  { name: "Café oscuro", hex: "#4b2e1e" },
  { name: "Cognac", hex: "#a15c30" },
  { name: "Nude", hex: "#e3bfa4" },
  { name: "Rojo", hex: "#c62828" },
  { name: "Vino/Burdeos", hex: "#6d1b2f" },
  { name: "Rosado", hex: "#f4a6c0" },
  { name: "Rosa palo", hex: "#dba8a0" },
  { name: "Fucsia", hex: "#d6336c" },
  { name: "Naranja", hex: "#e8720c" },
  { name: "Coral", hex: "#ff6f61" },
  { name: "Amarillo", hex: "#f5c518" },
  { name: "Mostaza", hex: "#c9a227" },
  { name: "Dorado", hex: "#c9a86a" },
  { name: "Verde", hex: "#2e7d32" },
  { name: "Verde militar", hex: "#556b2f" },
  { name: "Verde esmeralda", hex: "#0f6b4c" },
  { name: "Verde menta", hex: "#a8d8c9" },
  { name: "Turquesa", hex: "#1abc9c" },
  { name: "Azul", hex: "#1565c0" },
  { name: "Azul marino", hex: "#1a2a4a" },
  { name: "Azul cielo", hex: "#8ecae6" },
  { name: "Morado", hex: "#6a3d9a" },
  { name: "Lila", hex: "#c9a8e0" },
  { name: "Plateado", hex: "#c0c0c0" },
];

function sanitizeSkuSegment(value, maxLength) {
  return normalizeString(value)
    .toUpperCase()
    .normalize("NFD")
    .replace(new RegExp("[\\u0300-\\u036f]", "g"), "")
    .replace(/[^A-Z0-9]/g, "")
    .slice(0, maxLength);
}

function getVariantAttributeValue(attributes = [], attributeName) {
  const attribute = (attributes || []).find(
    (item) => normalizeKey(item?.name) === normalizeKey(attributeName)
  );

  // Se prefiere el nombre legible (p. ej. "Vino/Burdeos") sobre el valor
  // crudo (el hexadecimal del color), para que el SKU generado sirva como
  // referencia real de la variante en vez de un código sin sentido.
  return attribute?.label || attribute?.value || "";
}

function buildSkuPreview({ categoryCode, brandCode, variant, sequence }) {
  const colorValue = getVariantAttributeValue(variant?.attributes, "Color");
  const tallaValue = getVariantAttributeValue(variant?.attributes, "Talla");

  const categorySegment = sanitizeSkuSegment(categoryCode, 2) || "GE";
  const brandSegment = sanitizeSkuSegment(brandCode, 2) || "SM";
  const colorSegment = sanitizeSkuSegment(colorValue, 6) || "UNI";
  const tallaSegment = sanitizeSkuSegment(tallaValue, 4) || "UN";

  const sequenceSegment =
    sequence === null || sequence === undefined
      ? "####"
      : String(sequence).padStart(4, "0");

  return `${categorySegment}-${brandSegment}-${colorSegment}-${tallaSegment}-${sequenceSegment}`;
}

function getVariantKey(attributes = []) {
  return attributes
    .map((attribute) =>
      [
        normalizeKey(attribute.name),
        getEntityId(attribute.schema),
        getEntityId(attribute.classification),
        normalizeKey(attribute.value),
      ].join(":")
    )
    .sort()
    .join("|");
}

function getVariantLabel(variant) {
  const attributes = Array.isArray(variant?.attributes)
    ? variant.attributes
    : [];

  if (attributes.length === 0) {
    return "Variante única";
  }

  const sizeAttributes = attributes.filter(
    (attribute) => normalizeKey(attribute.name) === normalizeKey("Talla")
  );
  const otherAttributes = attributes.filter(
    (attribute) => normalizeKey(attribute.name) !== normalizeKey("Talla")
  );

  const formatAttribute = (attribute) => {
    const name = attribute.name || "Atributo";
    const value = attribute.label || attribute.value || "—";

    return `${name}: ${value}`;
  };

  const parts = [];

  if (sizeAttributes.length > 0) {
    parts.push(sizeAttributes.map(formatAttribute).join(" · "));
  }

  if (otherAttributes.length > 0) {
    parts.push(otherAttributes.map(formatAttribute).join(" · "));
  }

  return parts.join("  |  ");
}

function getVariantsMap(variants = []) {
  return new Map(
    variants.map((variant) => [
      getVariantKey(variant.attributes || []),
      variant,
    ])
  );
}

function createAttributeCombinations(groups) {
  const validGroups = groups.filter(
    (group) =>
      normalizeString(group?.name) &&
      Array.isArray(group?.values) &&
      group.values.length > 0
  );

  if (validGroups.length === 0) {
    return [[]];
  }

  return validGroups.reduce(
    (combinations, group) =>
      combinations.flatMap((combinationsCurrent) =>
        group.values.map((attribute) => [
          ...combinationsCurrent,
          attribute,
        ])
      ),
    [[]]
  );
}

function createVariantFromCombination({
  attributes,
  previousVariant,
  baseVariant,
}) {
  return {
    sku: previousVariant?.sku || "",
    attributes,
    price: previousVariant?.price ?? baseVariant?.price ?? "",
    offerPrice:
      previousVariant?.offerPrice ?? baseVariant?.offerPrice ?? "",
    cost: previousVariant?.cost ?? baseVariant?.cost ?? "",
    stock: previousVariant?.stock ?? baseVariant?.stock ?? 0,
    lowStockAlert:
      previousVariant?.lowStockAlert ??
      baseVariant?.lowStockAlert ??
      0,
    enabled: previousVariant?.enabled ?? baseVariant?.enabled ?? true,
    images: Array.isArray(previousVariant?.images)
      ? previousVariant.images
      : Array.isArray(baseVariant?.images)
        ? baseVariant.images
        : [],
  };
}

export function CreateProductPage() {
  const navigate = useNavigate();
  const imagePreviewsRef = useRef([]);
  const seoTouchedRef = useRef({ metaTitle: false, metaDescription: false });

  const [formData, setFormData] = useState(INITIAL_VALUES);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [availableCharacteristics, setAvailableCharacteristics] = useState(
    []
  );
  const [availableIncludes, setAvailableIncludes] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);

  const [draftColor, setDraftColor] = useState("#000000");
  const [draftColorName, setDraftColorName] = useState("");
  const [isColorPickerOpen, setIsColorPickerOpen] = useState(false);

  const [selectedColors, setSelectedColors] = useState([]);

  const [selectedSizes, setSelectedSizes] = useState([]);
  const [draftSize, setDraftSize] = useState("");

  const [selectedVariantImageIndex, setSelectedVariantImageIndex] =
    useState(null);

  const [isLoadingCategories, setIsLoadingCategories] = useState(true);
  const [isLoadingBrands, setIsLoadingBrands] = useState(true);
  const [isLoadingCharacteristics, setIsLoadingCharacteristics] =
    useState(true);
  const [isLoadingIncludes, setIsLoadingIncludes] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [validationErrors, setValidationErrors] = useState({});
  const [serverError, setServerError] = useState("");

  const [nextSkuSequence, setNextSkuSequence] = useState(null);

  useEffect(() => {
    async function loadCategories() {
      try {
        setIsLoadingCategories(true);
        setServerError("");

        const categoryController = new Category();

        const result = await categoryController.getCategories(1, 100, {
          visible: true,
        });

        setCategories(getCategoriesResponse(result));
      } catch (errorResponse) {
        console.error("loadCategories error:", errorResponse);

        setCategories([]);
        setServerError(
          errorResponse?.msg ||
            "No fue posible cargar las categorías disponibles."
        );
      } finally {
        setIsLoadingCategories(false);
      }
    }

    loadCategories();
  }, []);

  useEffect(() => {
    async function loadBrands() {
      try {
        setIsLoadingBrands(true);
        setBrands(await fetchClassificationsBySchema(SSMARCA));
      } catch (errorResponse) {
        console.error("loadBrands error:", errorResponse);

        setBrands([]);
      } finally {
        setIsLoadingBrands(false);
      }
    }

    loadBrands();
  }, []);

  useEffect(() => {
    async function loadCharacteristics() {
      try {
        setIsLoadingCharacteristics(true);
        setAvailableCharacteristics(
          await fetchClassificationsBySchema(SSCARACTERISTICA)
        );
      } catch (errorResponse) {
        console.error("loadCharacteristics error:", errorResponse);

        setAvailableCharacteristics([]);
      } finally {
        setIsLoadingCharacteristics(false);
      }
    }

    loadCharacteristics();
  }, []);

  useEffect(() => {
    async function loadIncludes() {
      try {
        setIsLoadingIncludes(true);
        setAvailableIncludes(await fetchClassificationsBySchema(SSINCLUYE));
      } catch (errorResponse) {
        console.error("loadIncludes error:", errorResponse);

        setAvailableIncludes([]);
      } finally {
        setIsLoadingIncludes(false);
      }
    }

    loadIncludes();
  }, []);

  useEffect(() => {
    imagePreviewsRef.current = imagePreviews;
  }, [imagePreviews]);

  useEffect(() => {
    return () => {
      imagePreviewsRef.current.forEach((imagePreview) => {
        if (imagePreview.path?.startsWith("blob:")) {
          URL.revokeObjectURL(imagePreview.path);
        }
      });
    };
  }, []);

  const availableCategories = useMemo(() => {
    return categories.filter((category) => category.visible !== false);
  }, [categories]);

  const availableBrands = useMemo(() => {
    return brands;
  }, [brands]);

  const selectedCategoryCode = useMemo(() => {
    return (
      availableCategories.find(
        (category) => getEntityId(category) === formData.category
      )?.id || ""
    );
  }, [availableCategories, formData.category]);

  const selectedBrandCode = useMemo(() => {
    return (
      availableBrands.find(
        (brand) => getEntityId(brand) === formData.brand
      )?.id || ""
    );
  }, [availableBrands, formData.brand]);

  useEffect(() => {
    if (!formData.category) {
      setNextSkuSequence(null);
      return;
    }

    let cancelled = false;

    async function loadNextSku() {
      try {
        const productController = new Product();

        const result = await productController.getNextSku(
          formData.category,
          formData.brand
        );

        if (!cancelled) {
          setNextSkuSequence(result?.nextSku?.nextSequence ?? null);
        }
      } catch (errorResponse) {
        console.error("getNextSku error:", errorResponse);

        if (!cancelled) {
          setNextSkuSequence(null);
        }
      }
    }

    loadNextSku();

    return () => {
      cancelled = true;
    };
  }, [formData.category, formData.brand]);

  const skuPreviews = useMemo(() => {
    let sequenceOffset = 0;

    return formData.variants.map((variant) => {
      if (variant.sku) {
        return buildSkuPreview({
          categoryCode: selectedCategoryCode,
          brandCode: selectedBrandCode,
          variant,
        });
      }

      const preview = buildSkuPreview({
        categoryCode: selectedCategoryCode,
        brandCode: selectedBrandCode,
        variant,
        sequence:
          nextSkuSequence === null
            ? null
            : nextSkuSequence + sequenceOffset,
      });

      sequenceOffset += 1;

      return preview;
    });
  }, [formData.variants, selectedCategoryCode, selectedBrandCode, nextSkuSequence]);

  const hasAttributeSelections = useMemo(() => {
    return selectedColors.length > 0 || selectedSizes.length > 0;
  }, [selectedColors.length, selectedSizes.length]);

  const combinationsCount = useMemo(() => {
    const nonEmptyGroupSizes = [
      selectedColors.length,
      selectedSizes.length,
    ].filter((count) => count > 0);

    return nonEmptyGroupSizes.reduce((total, count) => total * count, 1);
  }, [selectedColors.length, selectedSizes.length]);

  const selectedVariant = useMemo(() => {
    if (
      selectedVariantImageIndex === null ||
      !formData.variants[selectedVariantImageIndex]
    ) {
      return null;
    }

    return formData.variants[selectedVariantImageIndex];
  }, [formData.variants, selectedVariantImageIndex]);

  const hasValidationErrors = Object.values(validationErrors).some(Boolean);

  function clearFieldError(fieldName) {
    setValidationErrors((currentErrors) => ({
      ...currentErrors,
      [fieldName]: "",
    }));
  }

  function handleChange(event) {
    const { name, value, type, checked } = event.target;

    if (name === "metaTitle" || name === "metaDescription") {
      seoTouchedRef.current[name] = true;

      setFormData((currentData) => ({
        ...currentData,
        seo: {
          ...currentData.seo,
          [name]: value,
        },
      }));

      clearFieldError(name);
      return;
    }

    if (name === "description") {
      setFormData((currentData) => ({
        ...currentData,
        description: value,
        seo: {
          ...currentData.seo,
          metaDescription: seoTouchedRef.current.metaDescription
            ? currentData.seo.metaDescription
            : buildDefaultMetaDescription(value),
        },
      }));

      clearFieldError("description");
      return;
    }

    setFormData((currentData) => ({
      ...currentData,
      [name]: type === "checkbox" ? checked : value,
    }));

    clearFieldError(name);
  }

  function handleNameChange(event) {
    const name = event.target.value;

    setFormData((currentData) => ({
      ...currentData,
      name,
      slug: currentData.slug || createSlug(name),
      seo: {
        ...currentData.seo,
        metaTitle: seoTouchedRef.current.metaTitle
          ? currentData.seo.metaTitle
          : buildDefaultMetaTitle(name),
      },
    }));

    clearFieldError("name");
    clearFieldError("slug");
  }

  function handleVariantChange(index, event) {
    const { name, value, type, checked } = event.target;

    setFormData((currentData) => ({
      ...currentData,
      variants: currentData.variants.map((variant, variantIndex) =>
        variantIndex === index
          ? {
              ...variant,
              [name]: type === "checkbox" ? checked : value,
            }
          : variant
      ),
    }));

    clearFieldError(`variant_${index}_${name}`);
    clearFieldError("variants");
  }

  function handleOpenColorPicker() {
    setServerError("");
    setIsColorPickerOpen(true);
  }

  function handleCloseColorPicker() {
    setDraftColorName("");
    setIsColorPickerOpen(false);
  }

  function handleColorHexChange(event) {
    const value = event.target.value;

    setDraftColor(value);

    if (isHexColor(value)) {
      setServerError("");
    }
  }

  function handleAddColor() {
    const colorValue = normalizeString(draftColor).toLowerCase();
    const colorName = normalizeString(draftColorName);

    if (!isHexColor(colorValue)) {
      setServerError(
        "Selecciona o ingresa un color hexadecimal válido, por ejemplo #B33461."
      );
      return;
    }

    // El nombre es obligatorio: el color sirve como referencia para
    // identificar la variante (en el SKU, en el catálogo, en inventario),
    // y un código hexadecimal por sí solo no le dice nada a nadie.
    if (!colorName) {
      setServerError(
        "Ingresa un nombre para el color, por ejemplo: Vino, Azul marino."
      );
      return;
    }

    const alreadyExists = selectedColors.some(
      (color) => String(color.value).toLowerCase() === colorValue
    );

    if (alreadyExists) {
      setServerError("Ese color ya fue agregado.");
      return;
    }

    setSelectedColors((currentColors) => [
      ...currentColors,
      {
        schema: null,
        classification: null,
        name: "Color",
        value: colorValue,
        label: colorName,
      },
    ]);

    setDraftColorName("");
    setIsColorPickerOpen(false);
    setServerError("");
  }

  function handleRemoveColor(colorToRemove) {
    setSelectedColors((currentColors) =>
      currentColors.filter(
        (color) =>
          String(color.value).toLowerCase() !==
          String(colorToRemove.value).toLowerCase()
      )
    );

    setServerError("");
  }

  function handleSizeKeyDown(event) {
    if (event.key === "Enter") {
      event.preventDefault();
      handleAddSize();
    }
  }

  function handleAddSize() {
    const sizeValue = normalizeString(draftSize);

    if (!sizeValue) {
      setServerError("Ingresa una talla antes de agregarla.");
      return;
    }

    const alreadyExists = selectedSizes.some(
      (size) => normalizeKey(size.value) === normalizeKey(sizeValue)
    );

    if (alreadyExists) {
      setServerError(`La talla "${sizeValue}" ya fue agregada.`);
      return;
    }

    setSelectedSizes((currentSizes) => [
      ...currentSizes,
      {
        schema: null,
        classification: null,
        name: "Talla",
        value: sizeValue,
        label: sizeValue,
      },
    ]);

    setDraftSize("");
    setServerError("");
  }

  function handleRemoveSize(sizeToRemove) {
    setSelectedSizes((currentSizes) =>
      currentSizes.filter(
        (size) => normalizeKey(size.value) !== normalizeKey(sizeToRemove.value)
      )
    );

    setServerError("");
  }

  function handleToggleCharacteristic(classificationId) {
    setFormData((currentFormData) => {
      const alreadySelected =
        currentFormData.characteristics.includes(classificationId);

      return {
        ...currentFormData,
        characteristics: alreadySelected
          ? currentFormData.characteristics.filter(
              (id) => id !== classificationId
            )
          : [...currentFormData.characteristics, classificationId],
      };
    });
  }

  function handleGenerateVariants() {
    if (!hasAttributeSelections) {
      setServerError(
        "Agrega al menos un color o una talla para generar variantes."
      );
      return;
    }

    const combinations = createAttributeCombinations([
      {
        name: "Color",
        values: selectedColors,
      },
      {
        name: "Talla",
        values: selectedSizes,
      },
    ]);

    if (combinations.length > MAX_VARIANTS) {
      setServerError(
        `Esta configuración generaría ${combinations.length} variantes. El máximo permitido es ${MAX_VARIANTS}.`
      );
      return;
    }

    setFormData((currentData) => {
      const variantsMap = getVariantsMap(currentData.variants);
      const baseVariant = currentData.variants[0] || INITIAL_VARIANT;

      return {
        ...currentData,
        variants: combinations.map((attributes) => {
          const variantKey = getVariantKey(attributes);

          return createVariantFromCombination({
            attributes,
            previousVariant: variantsMap.get(variantKey),
            baseVariant,
          });
        }),
      };
    });

    clearFieldError("variants");
    setServerError("");
  }

  function handleResetVariants() {
    setFormData((currentData) => {
      const baseVariant = currentData.variants[0] || INITIAL_VARIANT;

      return {
        ...currentData,
        variants: [
          {
            ...INITIAL_VARIANT,
            ...baseVariant,
            sku: "",
            attributes: [],
            images: [],
          },
        ],
      };
    });

    setSelectedColors([]);
    setSelectedSizes([]);
    setDraftSize("");
    setDraftColor("#000000");
    setDraftColorName("");
    setIsColorPickerOpen(false);
    setSelectedVariantImageIndex(null);

    clearFieldError("variants");
    setServerError("");
  }

  function handleRemoveVariant(index) {
    if (formData.variants.length <= 1) {
      setServerError("El producto debe tener al menos una variante.");
      return;
    }

    setFormData((currentData) => ({
      ...currentData,
      variants: currentData.variants.filter(
        (_, variantIndex) => variantIndex !== index
      ),
    }));

    if (selectedVariantImageIndex === index) {
      setSelectedVariantImageIndex(null);
    }

    setServerError("");
  }

  function handleImageChange(event) {
    const selectedFiles = Array.from(event.target.files || []);

    if (selectedFiles.length === 0) {
      return;
    }

    const invalidFile = selectedFiles.find(
      (file) => !file.type.startsWith("image/")
    );

    if (invalidFile) {
      setServerError("Selecciona únicamente archivos de imagen válidos.");
      event.target.value = "";
      return;
    }

    const availableSlots = MAX_IMAGES - imagePreviews.length;

    if (availableSlots <= 0) {
      setServerError(`Solo puedes cargar hasta ${MAX_IMAGES} imágenes.`);
      event.target.value = "";
      return;
    }

    const filesToAdd = selectedFiles.slice(0, availableSlots);

    setImagePreviews((currentPreviews) => {
      const newPreviews = filesToAdd.map((file, index) =>
        createImagePreview(file, currentPreviews.length + index + 1)
      );

      const updatedPreviews = [...currentPreviews, ...newPreviews];

      setFormData((currentData) => ({
        ...currentData,
        images: updatedPreviews,
      }));

      return updatedPreviews;
    });

    if (selectedFiles.length > availableSlots) {
      setServerError(
        `Solo se agregaron ${availableSlots} imágenes. El máximo es ${MAX_IMAGES}.`
      );
    } else {
      setServerError("");
    }

    event.target.value = "";
  }

  function handleRemoveImage(localId) {
    setImagePreviews((currentPreviews) => {
      const imageToRemove = currentPreviews.find(
        (imagePreview) => imagePreview.localId === localId
      );

      if (imageToRemove?.path?.startsWith("blob:")) {
        URL.revokeObjectURL(imageToRemove.path);
      }

      const updatedPreviews = currentPreviews
        .filter((imagePreview) => imagePreview.localId !== localId)
        .map((imagePreview, index) => ({
          ...imagePreview,
          order: index + 1,
          isCover: index === 0,
        }));

      setFormData((currentData) => ({
        ...currentData,
        images: updatedPreviews,
        variants: currentData.variants.map((variant) => ({
          ...variant,
          images: Array.isArray(variant.images)
            ? variant.images.filter((imageId) => imageId !== localId)
            : [],
        })),
      }));

      return updatedPreviews;
    });
  }

  function handleSetCover(localId) {
    setImagePreviews((currentPreviews) => {
      const updatedPreviews = currentPreviews.map((imagePreview) => ({
        ...imagePreview,
        isCover: imagePreview.localId === localId,
      }));

      setFormData((currentData) => ({
        ...currentData,
        images: updatedPreviews,
      }));

      return updatedPreviews;
    });
  }

  function handleOpenVariantImages(variantIndex) {
    setSelectedVariantImageIndex(variantIndex);
  }

  function handleCloseVariantImages() {
    setSelectedVariantImageIndex(null);
  }

  function handleToggleVariantImage(variantIndex, imageLocalId) {
    setFormData((currentData) => ({
      ...currentData,
      variants: currentData.variants.map((variant, index) => {
        if (index !== variantIndex) {
          return variant;
        }

        const currentImages = Array.isArray(variant.images)
          ? variant.images
          : [];

        const images = currentImages.includes(imageLocalId)
          ? currentImages.filter((imageId) => imageId !== imageLocalId)
          : [...currentImages, imageLocalId];

        return {
          ...variant,
          images,
        };
      }),
    }));
  }

  function validateForm() {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = "El nombre del producto es obligatorio.";
    }

    if (!formData.slug.trim()) {
      errors.slug = "El slug o URL del producto es obligatorio.";
    }

    if (!formData.category) {
      errors.category = "La categoría es obligatoria.";
    }

    if (!Array.isArray(formData.variants) || formData.variants.length === 0) {
      errors.variants = "Debes registrar al menos una variante.";
      return errors;
    }

    const skuSet = new Set();

    formData.variants.forEach((variant, index) => {
      const sku = createSku(variant.sku);

      if (sku && skuSet.has(sku)) {
        errors[`variant_${index}_sku`] = "El SKU no puede repetirse.";
      } else if (sku) {
        skuSet.add(sku);
      }

      if (
        variant.price === "" ||
        !Number.isFinite(Number(variant.price)) ||
        Number(variant.price) < 0
      ) {
        errors[`variant_${index}_price`] =
          "El precio debe ser un número mayor o igual a 0.";
      }

      if (
        variant.offerPrice !== "" &&
        variant.offerPrice !== null &&
        variant.offerPrice !== undefined
      ) {
        if (
          !Number.isFinite(Number(variant.offerPrice)) ||
          Number(variant.offerPrice) < 0
        ) {
          errors[`variant_${index}_offerPrice`] =
            "El precio oferta debe ser un número mayor o igual a 0.";
        } else if (
          Number.isFinite(Number(variant.price)) &&
          Number(variant.offerPrice) > Number(variant.price)
        ) {
          errors[`variant_${index}_offerPrice`] =
            "El precio oferta no puede ser mayor al precio normal.";
        }
      }

      if (
        variant.stock === "" ||
        !Number.isInteger(Number(variant.stock)) ||
        Number(variant.stock) < 0
      ) {
        errors[`variant_${index}_stock`] =
          "El stock debe ser un número entero mayor o igual a 0.";
      }

      if (
        variant.lowStockAlert === "" ||
        !Number.isInteger(Number(variant.lowStockAlert)) ||
        Number(variant.lowStockAlert) < 0
      ) {
        errors[`variant_${index}_lowStockAlert`] =
          "La alerta de stock debe ser un número entero mayor o igual a 0.";
      }
    });

    if (formData.description.length > 5000) {
      errors.description =
        "La descripción no puede superar 5,000 caracteres.";
    }

    if (formData.seo.metaTitle.length > 60) {
      errors.metaTitle =
        "El título SEO no puede superar 60 caracteres.";
    }

    if (formData.seo.metaDescription.length > 160) {
      errors.metaDescription =
        "La descripción meta no puede superar 160 caracteres.";
    }

    return errors;
  }

  async function handleSubmit(event) {
    event.preventDefault();

    const errors = validateForm();

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      setServerError("");
      return;
    }

    try {
      setIsSubmitting(true);
      setServerError("");

      const productController = new Product();

      const isSingleVariant = formData.variants.length === 1;
      const allImageIds = formData.images
        .map((image) => image.localId)
        .filter(Boolean);

      await productController.createProduct({
        ...formData,
        id: formData.id.trim() || createProductId(formData.name),
        slug: createSlug(formData.slug || formData.name),
        brand: formData.brand || null,
        variants: formData.variants.map((variant) => ({
          ...variant,
          sku: createSku(variant.sku),
          attributes: (variant.attributes || []).map((attribute) => ({
            schema: getEntityId(attribute.schema) || null,
            classification: getEntityId(attribute.classification) || null,
            name: normalizeString(attribute.name),
            value: normalizeString(attribute.value),
            label: normalizeString(attribute.label),
          })),
          price: Number(variant.price),
          offerPrice:
            variant.offerPrice === "" ? null : Number(variant.offerPrice),
          cost: variant.cost === "" ? null : Number(variant.cost),
          stock: Number(variant.stock),
          lowStockAlert: Number(variant.lowStockAlert),
          images: isSingleVariant
            ? allImageIds
            : Array.isArray(variant.images)
            ? variant.images.filter(Boolean)
            : [],
        })),
      });

      navigate("/admin/products", {
        state: {
          success: "Producto creado correctamente.",
        },
      });
    } catch (errorResponse) {
      console.error("createProduct error:", errorResponse);

      if (errorResponse?.errors) {
        setValidationErrors(errorResponse.errors);
      }

      setServerError(
        errorResponse?.msg ||
          "No fue posible crear el producto. Intenta nuevamente."
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleCancel() {
    if (!isSubmitting) {
      navigate("/admin/products");
    }
  }

  return (
    <section className={styles.CreateProductPage}>
      <header className={styles.PageHeader}>
        <div className={styles.TitleSection}>
          <button
            type="button"
            className={styles.BackButton}
            onClick={handleCancel}
            disabled={isSubmitting}
            aria-label="Volver a productos"
          >
            ←
          </button>

          <div>
            <p className={styles.Breadcrumb}>Productos / Nuevo producto</p>
            <h1>Nuevo producto</h1>

            <p className={styles.Subtitle}>
              Registra la información comercial, imágenes y variantes del
              producto.
            </p>
          </div>
        </div>

        <div className={styles.HeaderActions}>
          <button
            type="button"
            className={styles.CancelButton}
            onClick={handleCancel}
            disabled={isSubmitting}
          >
            Cancelar
          </button>

          <button
            type="submit"
            form="create-product-form"
            className={styles.PublishButton}
            disabled={isSubmitting || isLoadingCategories}
          >
            {isSubmitting ? "Guardando..." : "Guardar producto"}
          </button>
        </div>
      </header>

      {hasValidationErrors && (
        <div className={styles.ValidationMessage} role="alert">
          <span className={styles.MessageIcon} aria-hidden="true">
            !
          </span>

          <div>
            <strong>Revisa los campos requeridos.</strong>

            <span>
              Corrige los errores marcados antes de guardar el producto.
            </span>
          </div>
        </div>
      )}

      {serverError && (
        <div className={styles.ServerErrorMessage} role="alert">
          <span className={styles.MessageIcon} aria-hidden="true">
            !
          </span>

          <span>{serverError}</span>
        </div>
      )}

      <form
        id="create-product-form"
        className={styles.FormContent}
        onSubmit={handleSubmit}
      >
        <section className={styles.BasicInformationCard}>
          <header className={styles.SectionHeader}>
            <div>
              <h2>Información básica</h2>
              <p>Define los datos principales del producto.</p>
            </div>
          </header>

          <div className={styles.FormGrid}>
            <div className={styles.FormField}>
              <label htmlFor="name">
                Nombre del producto <span>*</span>
              </label>

              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleNameChange}
                placeholder="Ej. Bolso Elegance Negro"
                error={Boolean(validationErrors.name)}
                disabled={isSubmitting}
              />

              {validationErrors.name && (
                <span className={styles.FieldError}>
                  {validationErrors.name}
                </span>
              )}
            </div>

            <div className={styles.FormField}>
              <label htmlFor="category">
                Categoría <span>*</span>
              </label>

              <select
                id="category"
                name="category"
                value={formData.category}
                onChange={handleChange}
                className={validationErrors.category ? styles.error : ""}
                disabled={isSubmitting || isLoadingCategories}
              >
                <option value="">
                  {isLoadingCategories
                    ? "Cargando categorías..."
                    : "Selecciona una categoría"}
                </option>

                {availableCategories.map((category) => (
                  <option
                    key={getEntityId(category)}
                    value={getEntityId(category)}
                  >
                    {getCategoryDisplayName(category)}
                  </option>
                ))}
              </select>

              {validationErrors.category && (
                <span className={styles.FieldError}>
                  {validationErrors.category}
                </span>
              )}
            </div>

            <div className={styles.FormField}>
              <label htmlFor="brand">Marca</label>

              <select
                id="brand"
                name="brand"
                value={formData.brand}
                onChange={handleChange}
                className={validationErrors.brand ? styles.error : ""}
                disabled={isSubmitting || isLoadingBrands}
              >
                <option value="">
                  {isLoadingBrands ? "Cargando marcas..." : "Sin marca"}
                </option>

                {availableBrands.map((brand) => (
                  <option key={getEntityId(brand)} value={getEntityId(brand)}>
                    {brand?.name || ""}
                  </option>
                ))}
              </select>

              {validationErrors.brand ? (
                <span className={styles.FieldError}>
                  {validationErrors.brand}
                </span>
              ) : (
                <span className={styles.FieldHelp}>
                  Opcional. Selecciona la marca del producto.
                </span>
              )}
            </div>

            <div className={styles.FormField}>
              <label htmlFor="slug">
                Slug / URL <span>*</span>
              </label>

              <Input
                id="slug"
                name="slug"
                value={formData.slug}
                onChange={handleChange}
                placeholder="Ej. bolso-elegance-negro"
                error={Boolean(validationErrors.slug)}
                disabled={isSubmitting}
              />

              {validationErrors.slug ? (
                <span className={styles.FieldError}>
                  {validationErrors.slug}
                </span>
              ) : (
                <span className={styles.FieldHelp}>
                  Se usará en la URL de la tienda.
                </span>
              )}
            </div>

            <div className={`${styles.FormField} ${styles.FullWidth}`}>
              <label htmlFor="description">Descripción</label>

              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                className={validationErrors.description ? styles.error : ""}
                placeholder="Describe el producto, materiales, estilo y características principales..."
                maxLength="5000"
                disabled={isSubmitting}
              />

              <div className={styles.TextAreaFooter}>
                {validationErrors.description ? (
                  <span className={styles.FieldError}>
                    {validationErrors.description}
                  </span>
                ) : (
                  <span />
                )}

                <span>{formData.description.length} / 5000</span>
              </div>
            </div>

            <div className={`${styles.FormField} ${styles.FullWidth}`}>
              <label>Características</label>

              <p className={styles.FieldHelp}>
                Costuras, herrajes, materiales y similares. Se
                administran desde el módulo de Clasificaciones,
                esquema "Características".
              </p>

              {isLoadingCharacteristics ? (
                <span className={styles.FieldHelp}>
                  Cargando características...
                </span>
              ) : availableCharacteristics.length === 0 ? (
                <div className={styles.EmptyConfiguration}>
                  Todavía no hay características registradas. Créalas
                  desde el módulo de Clasificaciones, bajo el esquema
                  "Características".
                </div>
              ) : (
                <div className={styles.CharacteristicsGrid}>
                  {availableCharacteristics.map((characteristic) => {
                    const characteristicId = getEntityId(characteristic);
                    const isSelected = formData.characteristics.includes(
                      characteristicId
                    );

                    return (
                      <button
                        key={characteristicId}
                        type="button"
                        className={`${styles.CharacteristicChip} ${
                          isSelected ? styles.CharacteristicChipActive : ""
                        }`}
                        onClick={() =>
                          handleToggleCharacteristic(characteristicId)
                        }
                        disabled={isSubmitting}
                      >
                        {isSelected ? "✓ " : ""}
                        {characteristic?.name || ""}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div className={`${styles.FormField} ${styles.FullWidth}`}>
              <label>Incluye</label>

              <p className={styles.FieldHelp}>
                Qué trae la pieza: caja, dust bag, etiquetas, tarjeta,
                shopping bag, etc. Se administra desde el módulo de
                Clasificaciones, esquema "Incluye".
              </p>

              {isLoadingIncludes ? (
                <span className={styles.FieldHelp}>Cargando...</span>
              ) : availableIncludes.length === 0 ? (
                <div className={styles.EmptyConfiguration}>
                  Todavía no hay elementos de "Incluye" registrados.
                  Créalos desde el módulo de Clasificaciones, bajo el
                  esquema "Incluye".
                </div>
              ) : (
                <div className={styles.CharacteristicsGrid}>
                  {availableIncludes.map((item) => {
                    const itemId = getEntityId(item);
                    const isSelected =
                      formData.characteristics.includes(itemId);

                    return (
                      <button
                        key={itemId}
                        type="button"
                        className={`${styles.CharacteristicChip} ${
                          isSelected ? styles.CharacteristicChipActive : ""
                        }`}
                        onClick={() => handleToggleCharacteristic(itemId)}
                        disabled={isSubmitting}
                      >
                        {isSelected ? "✓ " : ""}
                        {item?.name || ""}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </section>

        <section className={styles.VariantsCard}>
          <header className={styles.SectionHeader}>
            <div>
              <h2>Variantes</h2>

              <p>
                Selecciona colores y define la talla; luego genera todas
                las combinaciones.
              </p>
            </div>

            <span className={styles.VariantsCounter}>
              {formData.variants.length}{" "}
              {formData.variants.length === 1 ? "variante" : "variantes"}
            </span>
          </header>

          <div className={styles.VariantsContent}>
            <div className={styles.VariantConfigurationGrid}>
            <section className={styles.VariantConfigurationCard}>
              <div className={styles.VariantConfigurationHeader}>
                <div>
                  <h3>Colores</h3>
                  <p>Agrega los colores disponibles para el producto.</p>
                </div>

                <button
                  type="button"
                  className={styles.OpenColorPickerButton}
                  onClick={handleOpenColorPicker}
                  disabled={isSubmitting}
                >
                  <span
                    className={styles.CurrentColorPreview}
                    style={{ backgroundColor: draftColor }}
                    aria-hidden="true"
                  />
                  Agregar color
                </button>
              </div>

              {isColorPickerOpen && (
                <div className={styles.ColorPickerPanel}>
                  <div className={styles.PresetColorGrid}>
                    {PRESET_COLORS.map((preset) => (
                      <button
                        key={preset.hex}
                        type="button"
                        className={styles.PresetColorSwatch}
                        style={{ backgroundColor: preset.hex }}
                        title={preset.name}
                        aria-label={preset.name}
                        onClick={() => {
                          setDraftColor(preset.hex);
                          setDraftColorName(preset.name);
                          setServerError("");
                        }}
                        disabled={isSubmitting}
                      />
                    ))}
                  </div>

                  <div className={styles.ColorPickerMain}>
                    <HexColorPicker
                      color={draftColor}
                      onChange={(color) => {
                        setDraftColor(color);
                        setServerError("");
                      }}
                    />
                  </div>

                  <div className={styles.ColorPickerFields}>
                    <label htmlFor="variant-color-hex">
                      Código hexadecimal
                    </label>

                    <div className={styles.HexInputRow}>
                      <span
                        className={styles.HexPreview}
                        style={{ backgroundColor: draftColor }}
                        aria-hidden="true"
                      />

                      <input
                        id="variant-color-hex"
                        type="text"
                        value={draftColor}
                        onChange={handleColorHexChange}
                        placeholder="#B33461"
                        maxLength={7}
                        disabled={isSubmitting}
                      />
                    </div>

                    <label htmlFor="variant-color-name">
                      Nombre del color <small>(obligatorio)</small>
                    </label>

                    <Input
                      id="variant-color-name"
                      value={draftColorName}
                      onChange={(event) =>
                        setDraftColorName(event.target.value)
                      }
                      placeholder="Ej. Vino, Azul marino"
                      required
                      disabled={isSubmitting}
                    />

                    <div className={styles.ColorPickerActions}>
                      <button
                        type="button"
                        className={styles.CancelColorButton}
                        onClick={handleCloseColorPicker}
                        disabled={isSubmitting}
                      >
                        Cancelar
                      </button>

                      <button
                        type="button"
                        className={styles.ConfirmColorButton}
                        onClick={handleAddColor}
                        disabled={isSubmitting}
                      >
                        Añadir color
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {selectedColors.length === 0 ? (
                <p className={styles.EmptySelection}>
                  Aún no agregaste colores para este producto.
                </p>
              ) : (
                <div className={styles.SelectedColors}>
                  {selectedColors.map((color, index) => (
                    <article
                      key={`${color.value}-${index}`}
                      className={styles.ColorChip}
                    >
                      <span
                        className={styles.ColorChipPreview}
                        style={{ backgroundColor: color.value }}
                        aria-hidden="true"
                      />

                      <span className={styles.ColorChipName}>
                        {color.label || String(color.value).toUpperCase()}
                      </span>

                      <button
                        type="button"
                        onClick={() => handleRemoveColor(color)}
                        disabled={isSubmitting}
                        aria-label={`Eliminar color ${
                          color.label || color.value
                        }`}
                      >
                        ×
                      </button>
                    </article>
                  ))}
                </div>
              )}
            </section>

            <section className={styles.VariantConfigurationCard}>
              <div className={styles.VariantConfigurationHeader}>
                <div>
                  <h3>Talla</h3>

                  <p>
                    Apartado especial para la talla del producto (aplica
                    según el tipo: ropa, calzado, lentes, etc.). Se ingresa
                    y se muestra por separado del resto de atributos.
                  </p>
                </div>
              </div>

              <div className={styles.SizeInputRow}>
                <div>
                  <label className={styles.SizeLabel} htmlFor="variant-size">
                    Talla
                  </label>

                  <Input
                    id="variant-size"
                    value={draftSize}
                    onChange={(event) => setDraftSize(event.target.value)}
                    onKeyDown={handleSizeKeyDown}
                    placeholder="Ej. M, 38, 19.4 x 10 x 5.3 cm"
                    disabled={isSubmitting}
                  />
                </div>

                <button
                  type="button"
                  className={styles.AddSizeButton}
                  onClick={handleAddSize}
                  disabled={isSubmitting}
                >
                  Agregar
                </button>
              </div>

              {selectedSizes.length === 0 ? (
                <p className={styles.EmptySelection}>
                  Aún no agregaste tallas para este producto.
                </p>
              ) : (
                <div className={styles.SelectedSizes}>
                  {selectedSizes.map((size) => (
                    <article key={size.value} className={styles.SizeChip}>
                      <span>{size.label}</span>

                      <button
                        type="button"
                        onClick={() => handleRemoveSize(size)}
                        disabled={isSubmitting}
                        aria-label={`Eliminar talla ${size.label}`}
                      >
                        ×
                      </button>
                    </article>
                  ))}
                </div>
              )}
            </section>
            </div>

            <div className={styles.CombinationsInfo}>
              Se generarán <strong>{combinationsCount}</strong>{" "}
              {combinationsCount === 1 ? "variante" : "variantes"}.
            </div>

            <button
              type="button"
              className={styles.GenerateVariantsButton}
              onClick={handleGenerateVariants}
              disabled={isSubmitting || !hasAttributeSelections}
            >
              Generar {combinationsCount}{" "}
              {combinationsCount === 1 ? "variante" : "variantes"}
            </button>

            {validationErrors.variants && (
              <span className={styles.FieldError}>
                {validationErrors.variants}
              </span>
            )}

            <div className={styles.VariantsTableContainer}>
              <table className={styles.VariantsTable}>
                <thead>
                  <tr>
                    <th>Variante</th>
                    <th>Imágenes</th>
                    <th title="Si lo dejas vacío se genera automático a partir de la categoría, la marca, el color y la talla. Siempre puedes escribir o modificar el tuyo.">
                      SKU
                    </th>
                    <th>Precio (Q)</th>
                    <th>Oferta (Q)</th>
                    <th title="Lo que cuesta la pieza. Se usa para calcular el Valor de inventario en el Resumen -- si se deja vacío, esta variante no suma ahí aunque tenga precio y stock.">
                      Costo (Q)
                    </th>
                    <th>Stock</th>
                    <th>Alerta</th>
                    <th>Habilitada</th>
                    <th aria-label="Acciones" />
                  </tr>
                </thead>

                <tbody>
                  {formData.variants.map((variant, index) => {
                    const assignedImages =
                      formData.variants.length === 1
                        ? imagePreviews
                        : imagePreviews.filter(
                            (imagePreview) =>
                              Array.isArray(variant.images) &&
                              variant.images.includes(imagePreview.localId)
                          );

                    return (
                      <tr
                        key={`${getVariantKey(
                          variant.attributes || []
                        )}-${index}`}
                      >
                        <td>
                          <span className={styles.VariantLabel}>
                            {getVariantLabel(variant)}
                          </span>
                        </td>

                        <td>
                          <button
                            type="button"
                            className={styles.ManageVariantImagesButton}
                            onClick={() => handleOpenVariantImages(index)}
                            disabled={isSubmitting}
                          >
                            {assignedImages.length > 0
                              ? `${assignedImages.length} ${
                                  assignedImages.length === 1
                                    ? "imagen"
                                    : "imágenes"
                                }`
                              : "Asignar imágenes"}
                          </button>

                          {assignedImages.length > 0 && (
                            <div className={styles.VariantImageThumbs}>
                              {assignedImages.slice(0, 3).map((image) => (
                                <img
                                  key={image.localId}
                                  src={image.path}
                                  alt=""
                                />
                              ))}

                              {assignedImages.length > 3 && (
                                <span>
                                  +{assignedImages.length - 3}
                                </span>
                              )}
                            </div>
                          )}
                        </td>

                        <td>
                          <Input
                            name="sku"
                            value={variant.sku}
                            placeholder={skuPreviews[index]}
                            onChange={(event) =>
                              handleVariantChange(index, event)
                            }
                            error={Boolean(
                              validationErrors[`variant_${index}_sku`]
                            )}
                            disabled={isSubmitting}
                          />

                          {validationErrors[`variant_${index}_sku`] && (
                            <span className={styles.FieldError}>
                              {validationErrors[`variant_${index}_sku`]}
                            </span>
                          )}
                        </td>

                        <td>
                          <Input
                            name="price"
                            type="number"
                            min="0"
                            step="0.01"
                            value={variant.price}
                            onChange={(event) =>
                              handleVariantChange(index, event)
                            }
                            error={Boolean(
                              validationErrors[`variant_${index}_price`]
                            )}
                            disabled={isSubmitting}
                          />

                          {validationErrors[`variant_${index}_price`] && (
                            <span className={styles.FieldError}>
                              {validationErrors[`variant_${index}_price`]}
                            </span>
                          )}
                        </td>

                        <td>
                          <Input
                            name="offerPrice"
                            type="number"
                            min="0"
                            step="0.01"
                            value={variant.offerPrice}
                            onChange={(event) =>
                              handleVariantChange(index, event)
                            }
                            error={Boolean(
                              validationErrors[`variant_${index}_offerPrice`]
                            )}
                            disabled={isSubmitting}
                          />

                          {validationErrors[
                            `variant_${index}_offerPrice`
                          ] && (
                            <span className={styles.FieldError}>
                              {
                                validationErrors[
                                  `variant_${index}_offerPrice`
                                ]
                              }
                            </span>
                          )}
                        </td>

                        <td>
                          <Input
                            name="cost"
                            type="number"
                            min="0"
                            step="0.01"
                            value={variant.cost}
                            onChange={(event) =>
                              handleVariantChange(index, event)
                            }
                            error={Boolean(
                              validationErrors[`variant_${index}_cost`]
                            )}
                            disabled={isSubmitting}
                          />

                          {validationErrors[`variant_${index}_cost`] && (
                            <span className={styles.FieldError}>
                              {validationErrors[`variant_${index}_cost`]}
                            </span>
                          )}
                        </td>

                        <td>
                          <Input
                            name="stock"
                            type="number"
                            min="0"
                            step="1"
                            value={variant.stock}
                            onChange={(event) =>
                              handleVariantChange(index, event)
                            }
                            error={Boolean(
                              validationErrors[`variant_${index}_stock`]
                            )}
                            disabled={isSubmitting}
                          />

                          {validationErrors[`variant_${index}_stock`] && (
                            <span className={styles.FieldError}>
                              {validationErrors[`variant_${index}_stock`]}
                            </span>
                          )}
                        </td>

                        <td>
                          <Input
                            name="lowStockAlert"
                            type="number"
                            min="0"
                            step="1"
                            value={variant.lowStockAlert}
                            onChange={(event) =>
                              handleVariantChange(index, event)
                            }
                            error={Boolean(
                              validationErrors[
                                `variant_${index}_lowStockAlert`
                              ]
                            )}
                            disabled={isSubmitting}
                          />

                          {validationErrors[
                            `variant_${index}_lowStockAlert`
                          ] && (
                            <span className={styles.FieldError}>
                              {
                                validationErrors[
                                  `variant_${index}_lowStockAlert`
                                ]
                              }
                            </span>
                          )}
                        </td>

                        <td>
                          <label className={styles.VariantEnabledSwitch}>
                            <input
                              name="enabled"
                              type="checkbox"
                              checked={variant.enabled !== false}
                              onChange={(event) =>
                                handleVariantChange(index, event)
                              }
                              disabled={isSubmitting}
                            />

                            <span />
                          </label>
                        </td>

                        <td>
                          <button
                            type="button"
                            className={styles.RemoveVariantButton}
                            onClick={() => handleRemoveVariant(index)}
                            disabled={
                              isSubmitting ||
                              formData.variants.length <= 1
                            }
                            aria-label={`Eliminar ${getVariantLabel(variant)}`}
                          >
                            ×
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <button
              type="button"
              className={styles.ResetVariantsButton}
              onClick={handleResetVariants}
              disabled={isSubmitting || formData.variants.length <= 1}
            >
              Restablecer a variante única
            </button>
          </div>
        </section>

        <section className={styles.ImagesCard}>
          <header className={styles.SectionHeader}>
            <div>
              <h2>Imágenes del producto</h2>

              <p>
                Sube imágenes y asígnalas posteriormente a una o más
                variantes.
              </p>
            </div>

            <span className={styles.ImagesCounter}>
              {imagePreviews.length} de {MAX_IMAGES} imágenes
            </span>
          </header>

          <div className={styles.ImagesContent}>
            <label className={styles.UploadArea} htmlFor="images">
              <span className={styles.UploadIcon}>↥</span>
              <strong>Selecciona imágenes</strong>

              <span>
                Formatos JPG, PNG o WEBP. Máximo {MAX_IMAGES} imágenes.
              </span>
            </label>

            <input
              id="images"
              name="images"
              type="file"
              accept="image/*"
              multiple
              onChange={handleImageChange}
              disabled={isSubmitting || imagePreviews.length >= MAX_IMAGES}
              hidden
            />

            {imagePreviews.length > 0 && (
              <div className={styles.ImagesGrid}>
                {imagePreviews.map((imagePreview) => (
                  <article
                    key={imagePreview.localId}
                    className={
                      imagePreview.isCover
                        ? `${styles.ImageCard} ${styles.CoverImageCard}`
                        : styles.ImageCard
                    }
                  >
                    <img src={imagePreview.path} alt={imagePreview.name} />

                    {imagePreview.isCover && (
                      <span className={styles.CoverBadge}>Portada</span>
                    )}

                    <div className={styles.ImageCardActions}>
                      {!imagePreview.isCover && (
                        <button
                          type="button"
                          onClick={() =>
                            handleSetCover(imagePreview.localId)
                          }
                          disabled={isSubmitting}
                        >
                          Usar como portada
                        </button>
                      )}

                      <button
                        type="button"
                        className={styles.RemoveImageButton}
                        onClick={() =>
                          handleRemoveImage(imagePreview.localId)
                        }
                        disabled={isSubmitting}
                      >
                        Eliminar
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>
        </section>

        <section className={styles.AdditionalInformationCard}>
          <header className={styles.SectionHeader}>
            <div>
              <h2>Información adicional</h2>
              <p>Opciones de publicación y configuración SEO.</p>
            </div>
          </header>

          <div className={styles.AdditionalContent}>
            <div className={styles.SwitchesRow}>
              <div className={styles.VisibilityField}>
                <span>Visible en tienda</span>

                <label className={styles.VisibilitySwitch}>
                  <input
                    name="visible"
                    type="checkbox"
                    checked={formData.visible}
                    onChange={handleChange}
                    disabled={isSubmitting}
                  />

                  <span />
                </label>

                <small>
                  El producto podrá mostrarse en el catálogo público.
                </small>
              </div>

              <div className={styles.VisibilityField}>
                <span>Producto destacado</span>

                <label className={styles.VisibilitySwitch}>
                  <input
                    name="featured"
                    type="checkbox"
                    checked={formData.featured}
                    onChange={handleChange}
                    disabled={isSubmitting}
                  />

                  <span />
                </label>

                <small>
                  Podrá aparecer en secciones destacadas de la tienda.
                </small>
              </div>
            </div>

            <section className={styles.SeoSection}>
              <h3 className={styles.SeoTitle}>SEO</h3>

              <p className={styles.SeoDescription}>
                Optimiza cómo se mostrará el producto en buscadores.
              </p>

              <div className={styles.SeoGrid}>
                <div className={styles.FormField}>
                  <label htmlFor="metaTitle">Título SEO</label>

                  <Input
                    id="metaTitle"
                    name="metaTitle"
                    value={formData.seo.metaTitle}
                    onChange={handleChange}
                    error={Boolean(validationErrors.metaTitle)}
                    disabled={isSubmitting}
                  />

                  <div className={styles.TextAreaFooter}>
                    {validationErrors.metaTitle ? (
                      <span className={styles.FieldError}>
                        {validationErrors.metaTitle}
                      </span>
                    ) : (
                      <span />
                    )}

                    <span>{formData.seo.metaTitle.length} / 60</span>
                  </div>
                </div>

                <div className={styles.FormField}>
                  <label htmlFor="metaDescription">
                    Descripción meta
                  </label>

                  <textarea
                    id="metaDescription"
                    name="metaDescription"
                    value={formData.seo.metaDescription}
                    onChange={handleChange}
                    className={
                      validationErrors.metaDescription ? styles.error : ""
                    }
                    maxLength="160"
                    disabled={isSubmitting}
                  />

                  <div className={styles.TextAreaFooter}>
                    {validationErrors.metaDescription ? (
                      <span className={styles.FieldError}>
                        {validationErrors.metaDescription}
                      </span>
                    ) : (
                      <span />
                    )}

                    <span>
                      {formData.seo.metaDescription.length} / 160
                    </span>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </section>
      </form>

      {selectedVariantImageIndex !== null && selectedVariant && (
        <div
          className={styles.VariantImagesModalOverlay}
          role="presentation"
          onMouseDown={handleCloseVariantImages}
        >
          <div
            className={styles.VariantImagesModal}
            role="dialog"
            aria-modal="true"
            aria-labelledby="variant-images-modal-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <header className={styles.VariantImagesModalHeader}>
              <div>
                <h2 id="variant-images-modal-title">
                  Imágenes de variante
                </h2>

                <p>{getVariantLabel(selectedVariant)}</p>
              </div>

              <button
                type="button"
                className={styles.CloseVariantImagesModalButton}
                onClick={handleCloseVariantImages}
                disabled={isSubmitting}
                aria-label="Cerrar selector de imágenes"
              >
                ×
              </button>
            </header>

            <div className={styles.VariantImagesModalContent}>
              {imagePreviews.length === 0 ? (
                <div className={styles.EmptyConfiguration}>
                  Primero sube imágenes en la sección “Imágenes del producto”.
                </div>
              ) : (
                <div className={styles.VariantImagesSelectionGrid}>
                  {imagePreviews.map((imagePreview) => {
                    const isSelected = Array.isArray(selectedVariant.images)
                      ? selectedVariant.images.includes(imagePreview.localId)
                      : false;

                    return (
                      <button
                        key={imagePreview.localId}
                        type="button"
                        className={
                          isSelected
                            ? `${styles.VariantImageOption} ${styles.VariantImageOptionSelected}`
                            : styles.VariantImageOption
                        }
                        onClick={() =>
                          handleToggleVariantImage(
                            selectedVariantImageIndex,
                            imagePreview.localId
                          )
                        }
                        disabled={isSubmitting}
                        aria-pressed={isSelected}
                      >
                        <img
                          src={imagePreview.path}
                          alt={imagePreview.name}
                        />

                        <span className={styles.VariantImageSelectionMark}>
                          {isSelected ? "✓" : ""}
                        </span>

                        <span className={styles.VariantImageOptionName}>
                          {imagePreview.name}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <footer className={styles.VariantImagesModalFooter}>
              <button
                type="button"
                className={styles.ConfirmColorButton}
                onClick={handleCloseVariantImages}
                disabled={isSubmitting}
              >
                Listo
              </button>
            </footer>
          </div>
        </div>
      )}
    </section>
  );
}